import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useTemplate } from "@/context/TemplateContext";

export default function Footer() {
  const { siteConfig } = useSiteConfig();
  const { templateId } = useTemplate();

  const phone = siteConfig?.contact_info?.phone || '';
  const email = siteConfig?.contact_info?.email || '';
  const address = siteConfig?.contact_info?.address;
  const companyName = siteConfig?.contact_info?.company_name;
  const icp = siteConfig?.icp || '';
  const icpUrl = siteConfig?.icp_url || '';
  const displayName = siteConfig?.site_name || '';
  const siteName = siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const footerInfo = siteConfig?.footer_info || '';

  // 默认底部菜单
  const defaultMenus = [
    { id: "m1", label: "首页", href: "/", external: false, enabled: true },
    { id: "m2", label: "发布信息", href: "/publish", external: false, enabled: true },
    { id: "m3", label: "开通VIP", href: "/vip", external: false, enabled: true },
    { id: "m4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
    { id: "m5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
    { id: "m6", label: "关于我们", href: "/page/about", external: false, enabled: true },
  ];

  const hasFooterMenus = siteConfig?.footer_menus && Array.isArray(siteConfig.footer_menus) && siteConfig.footer_menus.length > 0;
  const menus = hasFooterMenus
    ? siteConfig.footer_menus.filter((m: any) => m.enabled !== false)
    : defaultMenus;

  const currentYear = new Date().getFullYear();
  const copyrightOwner = companyName || displayName || siteName;
  const copyright = copyrightOwner ? `© ${currentYear} ${copyrightOwner} 版权所有` : `© ${currentYear} 版权所有`;

  // ========== 深色商务模版底部 ==========
  if (templateId === "dark") {
    return (
      <footer className="bg-gray-950 border-t border-gray-800 text-gray-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* 品牌 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              {logo && <img src={logo} alt={displayName || siteName} className="h-8 w-8 object-contain rounded-lg" />}
              {(displayName || siteName) && <span className="text-lg font-bold text-white">{displayName || siteName}</span>}
            </div>
            {footerInfo && <p className="text-sm text-gray-500 leading-relaxed">{footerInfo}</p>}
          </div>

          {/* 快捷链接 */}
          <div>
            <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-4">快捷链接</h4>
            <ul className="space-y-2">
              {menus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-gray-500 hover:text-amber-400 transition-colors">{item.label}</a>
                  ) : (
                    <Link to={item.href} className="text-sm text-gray-500 hover:text-amber-400 transition-colors">{item.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 联系方式 */}
          <div>
            <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-4">联系我们</h4>
            <ul className="space-y-2.5">
              {phone && <li><a href={`tel:${phone}`} className="text-sm text-gray-500 hover:text-amber-400 transition-colors flex items-center gap-2"><i className="ri-phone-line text-amber-500/50" />{phone}</a></li>}
              {email && <li><a href={`mailto:${email}`} className="text-sm text-gray-500 hover:text-amber-400 transition-colors flex items-center gap-2"><i className="ri-mail-line text-amber-500/50" />{email}</a></li>}
              {address && <li className="text-sm text-gray-500 flex items-center gap-2"><i className="ri-map-pin-line text-amber-500/50" />{address}</li>}
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span className="text-xs text-gray-600">{copyright}</span>
            {icp && <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer" className="text-xs text-gray-600 hover:text-amber-400 transition-colors">{icp}</a>}
          </div>
        </div>
      </footer>
    );
  }

  // ========== 清新绿意模版底部 ==========
  if (templateId === "green") {
    return (
      <footer className="bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                {logo && <img src={logo} alt={displayName || siteName} className="h-8 w-8 object-contain rounded-lg" />}
                {(displayName || siteName) && <span className="text-lg font-bold text-emerald-100">{displayName || siteName}</span>}
              </div>
              {footerInfo && <p className="text-sm text-emerald-200/70 leading-relaxed">{footerInfo}</p>}
              {!footerInfo && <p className="text-sm text-emerald-200/70 leading-relaxed">汇聚优质服务信息，让每一次连接都充满生机。</p>}
            </div>
            <div>
              <h4 className="text-xs font-bold text-lime-300 uppercase tracking-wider mb-4">快速链接</h4>
              <div className="grid grid-cols-2 gap-2">
                {menus.map((item: any) => (
                  <div key={item.id}>
                    {item.external ? (
                      <a href={item.href} target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-emerald-200/70 hover:text-lime-300 transition-colors">{item.label}</a>
                    ) : (
                      <Link to={item.href} className="text-sm text-emerald-200/70 hover:text-lime-300 transition-colors">{item.label}</Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-lime-300 uppercase tracking-wider mb-4">联系我们</h4>
              <ul className="space-y-2.5">
                {phone && <li><a href={`tel:${phone}`} className="flex items-center gap-2 text-sm text-emerald-200/70 hover:text-lime-300 transition-colors"><i className="ri-phone-line" />{phone}</a></li>}
                {email && <li><a href={`mailto:${email}`} className="flex items-center gap-2 text-sm text-emerald-200/70 hover:text-lime-300 transition-colors"><i className="ri-mail-line" />{email}</a></li>}
                {address && <li className="flex items-center gap-2 text-sm text-emerald-200/70"><i className="ri-map-pin-line" />{address}</li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-emerald-700/30 pt-5 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span className="text-xs text-emerald-300/50">{copyright}</span>
            {icp && <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer" className="text-xs text-emerald-300/50 hover:text-lime-300 transition-colors">{icp}</a>}
          </div>
        </div>
      </footer>
    );
  }

  // ========== 现代蓝色模版底部 ==========
  if (templateId === "modern") {
    return (
      <footer className="bg-gradient-to-br from-slate-800 via-slate-900 to-indigo-950 text-white">
        {/* 主体区 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* 品牌信息 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              {logo && (
                <img src={logo} alt={displayName || siteName} className="h-8 w-8 object-contain rounded-lg" />
              )}
              {(displayName || siteName) && (
                <span className="text-lg font-bold text-white">{displayName || siteName}</span>
              )}
            </div>
            {footerInfo && <p className="text-sm text-slate-400 leading-relaxed">{footerInfo}</p>}
            {!footerInfo && (
              <p className="text-sm text-slate-400 leading-relaxed">
                汇聚优质商业服务信息，连接服务商与客户，让商业合作更高效。
              </p>
            )}
          </div>

          {/* 快速链接 */}
          <div>
            <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-4">快速链接</h4>
            <ul className="space-y-2.5">
              {menus.slice(0, 4).map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-slate-400 hover:text-blue-400 transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm text-slate-400 hover:text-blue-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 更多链接 */}
          <div>
            <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-4">更多链接</h4>
            <ul className="space-y-2.5">
              {menus.slice(4).map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-slate-400 hover:text-blue-400 transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm text-slate-400 hover:text-blue-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 联系方式 */}
          <div>
            <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-4">联系我们</h4>
            <ul className="space-y-3">
              {phone && (
                <li>
                  <a href={`tel:${phone}`} className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-blue-400 transition-colors group">
                    <div className="w-8 h-8 rounded-lg bg-slate-700/50 group-hover:bg-blue-600/20 flex items-center justify-center shrink-0 transition-colors">
                      <i className="ri-phone-line text-blue-400" />
                    </div>
                    <span>{phone}</span>
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={`mailto:${email}`} className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-blue-400 transition-colors group">
                    <div className="w-8 h-8 rounded-lg bg-slate-700/50 group-hover:bg-blue-600/20 flex items-center justify-center shrink-0 transition-colors">
                      <i className="ri-mail-line text-blue-400" />
                    </div>
                    <span>{email}</span>
                  </a>
                </li>
              )}
              {address && (
                <li>
                  <div className="flex items-center gap-2.5 text-sm text-slate-400 group">
                    <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-blue-400" />
                    </div>
                    <span>{address}</span>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* 底栏 */}
        <div className="border-t border-slate-700/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-slate-500">{copyright}</span>
            {icp && (
              <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer" className="text-xs text-slate-500 hover:text-blue-400 transition-colors">
                {icp}
              </a>
            )}
          </div>
        </div>
      </footer>
    );
  }

  // ========== 经典橙色模版底部（默认） ==========
  return (
    <footer className="bg-[#F5F0EB] border-t border-[#E8DDD4]">
      {/* Main row */}
      <div className="w-full px-6 md:px-10 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Left: Brand + Contact */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <div className="flex items-center gap-2">
              {logo && (
                <img
                  src={logo}
                  alt={displayName || siteName}
                  className="h-7 w-7 object-contain"
                />
              )}
              {displayName && <span className="text-sm font-bold text-gray-700">{displayName}</span>}
            </div>
            <div className="flex items-center gap-4">
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-orange-500 transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-xs" />
                  </div>
                  {phone}
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-orange-500 transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-xs" />
                  </div>
                  {email}
                </a>
              )}
              {address && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xs" />
                  </div>
                  {address}
                </div>
              )}
            </div>
          </div>

          {/* Right: Nav menus */}
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {menus.map((item: any) =>
              item.external ? (
                <a
                  key={item.id}
                  href={item.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-orange-500 transition-colors whitespace-nowrap"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.id}
                  to={item.href}
                  className="text-xs text-gray-500 hover:text-orange-500 transition-colors whitespace-nowrap"
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#E0D5CC]">
        <div className="w-full px-6 md:px-10 py-3 flex flex-col sm:flex-row items-center justify-center gap-3">
          <span className="text-xs text-gray-400">{copyright}</span>
          {icp && (
            <a
              href={icpUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              {icp}
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
